import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useNewOfferLink } from '@Atomic/organisms/profile/hooks';
import { Button } from '@Atomic/atoms/Button';
import { Title } from '@Atomic/atoms/Headline';
import { Icons } from '@Atomic/atoms/Icons';

import emptyBoxImg from './img/empty-box.png';
import styles from './emptySearchBlock.module.scss';

type EmptySearchBlockProps = {
  loading: boolean;
  reducedPrecision: boolean;
  offersQuantity: number;
  isFallbackActive?: boolean;
  type?: 'category' | 'search' | 'sellerSelection';
};

const TITLE_KEY = {
  category: 'emptyCategory.title',
  search: 'emptySearch.title',
  sellerSelection: 'sellerSelection.emptyTitle',
} as const;

const TEXT_KEY = {
  category: 'emptyCategory.text',
  search: 'emptySearch.text',
  sellerSelection: 'sellerSelection.emptyText',
} as const;

export const EmptySearchBlock = ({
  loading,
  reducedPrecision,
  offersQuantity,
  isFallbackActive = false,
  type = 'search',
}: EmptySearchBlockProps): ReactNode => {
  const { t } = useTranslation();
  const newOfferLink = useNewOfferLink();

  if (loading) {
    return null;
  }

  if (offersQuantity > 0 && !reducedPrecision && !isFallbackActive) {
    return null;
  }

  const showFeedTitle = (reducedPrecision && offersQuantity > 0) || isFallbackActive;
  const isCategory = type === 'category';
  const isSeller = type === 'sellerSelection';
  const feedTitle = isSeller
    ? t('sellerSelection.recommendationsTitle')
    : t('emptySearch.feedTitle');

  return (
    <>
      <div className={styles.container}>
        <div className={styles.info}>
          <img
            className={styles.illustration}
            src={emptyBoxImg}
            alt=""
            role="presentation"
            width={80}
            height={80}
          />
          <div className={styles.message}>
            <p className={styles.title}>{t(TITLE_KEY[type])}</p>
            <p className={styles.text}>{t(TEXT_KEY[type])}</p>
          </div>
        </div>
        {isCategory && (
          <Button
            intent="neutral"
            presentation="filled"
            size="small"
            rounded
            iconLeft={<Icons.AddCircleFill width={16} height={16} />}
            to={newOfferLink}
          >
            {t('buttons.putAd')}
          </Button>
        )}
      </div>

      {showFeedTitle ? <Title.H2 className={styles.sectionTitle}>{feedTitle}</Title.H2> : null}
    </>
  );
};
